import { Component, ReactNode, Suspense, useEffect } from 'react';
import { RouteLoading } from './RouteLoading';

/**
 * 새 버전을 배포하면 옛 청크 파일이 서버에서 사라져, 열어 둔 옛 탭이 lazy 화면의 청크를
 * 받지 못한다(ChunkLoadError). 잡지 않으면 앱 전체가 하얗게 된다.
 *
 * 청크 오류면 새로고침을 딱 한 번만 시도하고(sessionStorage 표시), 그래도 실패하거나
 * 다른 오류면 새로고침 버튼이 있는 안내 화면을 보여 준다.
 * 새로고침 뒤 화면이 그려지고 잠시 오류가 없으면 표시를 지운다(`ChunkReloadReset`).
 */
const RELOAD_FLAG = 'scv:chunk-reload-attempted';

export const isChunkLoadError = (error: unknown): boolean => {
  const { name, message } = (error ?? {}) as Partial<Error>;
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(message ?? '')
  );
};

/** 표시를 처음 남겼으면 true. 이미 있거나 sessionStorage를 못 쓰면 false(무한 새로고침 방지). */
const markReloadAttempted = (): boolean => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, '1');
    return true;
  } catch {
    return false;
  }
};

const clearReloadAttempted = () => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // sessionStorage를 못 쓰면 표시도 남지 않았다
  }
};

const reloadPage = () => window.location.reload();

/**
 * 그려진 뒤 잠시 오류가 없을 때만 새로고침 표시를 지운다.
 * 공개 화면은 Router(eager) 안의 Suspense에서 따로 lazy로 받는다. 바깥이 그려졌다고 바로 지우면
 * 안쪽 청크가 또 실패할 때 다시 새로고침해 무한히 반복된다. 오류가 나면 경계가 이 컴포넌트를
 * 내려 타이머가 취소된다.
 */
const RELOAD_FLAG_CLEAR_DELAY_MS = 10_000;

export const ChunkReloadReset = () => {
  useEffect(() => {
    const timer = setTimeout(clearReloadAttempted, RELOAD_FLAG_CLEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  return null;
};

type Props = {
  children: ReactNode;
  /** 테스트에서 바꿔 끼운다. 기본은 location.reload(). */
  reload?: () => void;
};
type State = { error: Error | null; reloading: boolean };

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && markReloadAttempted()) {
      this.setState({ reloading: true });
      (this.props.reload ?? reloadPage)();
    }
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (reloading) return <RouteLoading />;

    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center space-y-4 px-4 text-center"
      >
        <h5 className="font-bold">화면을 불러오지 못했습니다</h5>
        <p className="text-14 text-gray-600">
          새 버전이 배포되었거나 네트워크가 불안정할 수 있습니다.
          <br />
          새로고침해 주세요.
        </p>
        <button
          type="button"
          className="button h-14 px-8 text-15 font-semibold"
          onClick={this.props.reload ?? reloadPage}
        >
          새로고침
        </button>
      </div>
    );
  }
}

/** lazy 화면을 감싼다: 받는 동안 로딩 표시, 청크 오류는 한 번 새로고침 뒤 안내 화면. */
export const LazyRouteBoundary = ({ children, reload }: Props) => (
  <ChunkErrorBoundary reload={reload}>
    <Suspense fallback={<RouteLoading />}>
      {children}
      <ChunkReloadReset />
    </Suspense>
  </ChunkErrorBoundary>
);
